import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import './lib/errorHandler'; // 导入错误处理器（自动初始化）
import { initGlobalErrorHandlers } from './lib/utils'; // 导入全局错误处理函数
import { initI18n } from './i18n/i18n'; // 导入多语言初始化函数
// Font Awesome 已通过 index.html CDN 引入，此处不再导入本地包（避免 pnpm 下构建解析失败）

// 开发模式下的额外检查
if (process.env.NODE_ENV === 'development') {
  console.log('🚀 指点地球村前端应用启动中...');
  console.log('📍 错误处理器已加载，异步响应错误将被自动处理');
}

// Font Awesome 已通过 npm 包导入，无需动态加载CDN
// 这样可以避免跨域问题和混合内容问题

  // 增强的错误边界组件
  class ErrorBoundary extends React.Component<{ children: React.ReactNode }, { hasError: boolean, error?: Error, errorInfo?: React.ErrorInfo }> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    console.error("应用组件错误:", error);
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("应用错误:", error);
    console.error("错误信息:", errorInfo);
    console.error("错误堆栈:", error.stack);
    
     // 分析错误类型，如果是机构端相关的错误，尝试清理可能有问题的认证数据
    if (error.message && (
        error.message.includes('Cannot read properties')
    )) {
      console.log("检测到可能与机构端认证相关的错误，尝试清理认证数据...");
      try {
        // 更加彻底地清理所有可能导致问题的认证数据
        localStorage.removeItem('auth');
        localStorage.removeItem('authToken');
        localStorage.removeItem('username');
        localStorage.removeItem('userRole');
        localStorage.removeItem('realNameVerified');
        localStorage.removeItem('pendingJoinCommunityId');
        localStorage.removeItem('selectedJobPosting');
        sessionStorage.clear();
        console.log("已清理所有认证相关数据");
      } catch (cleanupError) {
        console.error("清理认证数据时出错:", cleanupError);
      }
    }
    
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      // 移除加载指示器
      const loadingIndicator = document.getElementById('app-loading-indicator');
      if (loadingIndicator) {
        loadingIndicator.remove();
      }
      
      return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
          <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
            <i className="fa-solid fa-exclamation-triangle text-red-500 text-3xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">应用加载出错</h2>
          <p className="text-gray-600 mb-6 max-w-md text-center">
            很抱歉，应用在加载过程中遇到了问题。请检查以下几点：
            <br/><br/>
            1. 确保您使用的是有效的测试账号登录
            <br/>
            2. 请尝试清除浏览器缓存和本地存储后重新登录
            <br/>
            3. 如果问题持续，请使用下方按钮刷新页面或返回首页
          </p>
          {/* 显示详细错误信息（开发环境） */}
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <div className="bg-gray-800 text-gray-100 p-4 rounded-lg mb-6 max-w-md text-sm overflow-auto max-h-40">
              <p className="font-bold">错误信息:</p>
              <p>{this.state.error.message}</p>
              <p className="mt-2 font-bold">错误类型:</p>
              <p>{this.state.error.name}</p>
              {this.state.error.stack && (
                <>
                  <p className="mt-2 font-bold">错误堆栈:</p>
                  <pre className="whitespace-pre-wrap">{this.state.error.stack.substring(0, 200)}</pre>
                </>
              )}
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-4">
            <button 
                onClick={() => {
                  // 清除所有认证相关的本地存储
                  localStorage.removeItem('auth');
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('username');
                  localStorage.removeItem('userRole');
                  localStorage.removeItem('realNameVerified');
                  localStorage.removeItem('pendingJoinCommunityId');
                  localStorage.removeItem('selectedJobPosting');
                  sessionStorage.clear();
                window.location.reload();
              }}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <i className="fa-solid fa-rotate-right mr-2"></i>重置并刷新
            </button>
            <button 
              onClick={() => {
                  // 清除认证信息并返回管理端登录页
                  localStorage.removeItem('auth');
                  localStorage.removeItem('authToken');
                  localStorage.removeItem('userRole');
                window.location.href = '/admin/login';
              }}
              className="px-6 py-3 bg-white text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <i className="fa-solid fa-home mr-2"></i>前往管理端登录
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// 确保DOM元素存在后再渲染
const rootElement = document.getElementById('root');
if (!rootElement) {
  // 如果没有找到root元素，创建一个
  console.error('未找到root元素，创建默认root元素');
  const newRootElement = document.createElement('div');
  newRootElement.id = 'root';
  document.body.appendChild(newRootElement);
}

// 重新获取root元素（可能是创建的）
const rootElementFinal = document.getElementById('root');
if (!rootElementFinal) {
  // 如果仍然没有，显示错误页面
  document.body.innerHTML = `
    <div class="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
      <div class="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
        <i class="fa-solid fa-exclamation-triangle text-red-500 text-3xl"></i>
      </div>
      <h2 class="text-2xl font-bold text-gray-900 mb-2">无法渲染应用</h2>
      <p class="text-gray-600 mb-6 max-w-md text-center">
        无法找到或创建应用挂载点，请检查HTML结构是否正确。
      </p>
      <button onclick="window.location.reload()" class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
        <i class="fa-solid fa-rotate-right mr-2"></i>刷新页面
      </button>
    </div>
  `;
} else {
  // 正常渲染应用（HMR 时复用已有 root，避免 createRoot 重复调用警告）
  const container = rootElementFinal;
  type RootType = ReturnType<typeof ReactDOM.createRoot>;
  let root: RootType = (window as any).__reactRoot;
  if (!root) {
    root = ReactDOM.createRoot(container);
    (window as any).__reactRoot = root;
  }
  
  // 初始化全局错误处理 - 确保在应用渲染前初始化
  initGlobalErrorHandlers();

  // 初始化多语言系统
  initI18n();
  
  // 强制渲染一个默认的首页内容，确保页面不会空白
  const renderApp = () => {
    try {
      const appElement = (
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      );
      return appElement;
    } catch (error) {
      console.error('渲染App组件失败，使用备用渲染:', error);
      // 如果App组件渲染失败，显示一个简单的备用页面
      return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mb-6">
            <i className="fa-solid fa-globe text-blue-500 text-3xl"></i>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">欢迎访问指点地球村</h2>
          <p className="text-gray-600 mb-6 max-w-md text-center">
            应用正在初始化，请稍候...
          </p>
          <button 
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <i className="fa-solid fa-rotate-right mr-2"></i>刷新页面
          </button>
        </div>
      );
    }
  };
  
    // 添加全局错误捕获，特别是针对React的合成事件错误
    window.addEventListener('error', (event) => {
     console.error('全局错误捕获:', event.error);
     // 检查是否是由于React事件系统导致的卡死
     if (event.error && event.error.message) {
       const errorMessage = event.error.message;
       console.error('检测到错误类型:', event.error.name, '错误信息:', errorMessage);
       
         // 检测并专门处理Cannot go back错误
        if (errorMessage.includes('Cannot go back')) {
          console.error('捕获到Cannot go back错误，防止页面崩溃');
          try {
            // 检查当前是否已经在首页，避免循环重定向
            if (window.location.pathname === '/admin' || window.location.pathname === '/admin/login') {
              console.log('当前已在管理端，不进行重定向操作');
              // 显示一个友好的提示，而不是尝试重定向
              const showErrorNotification = () => {
                const notification = document.createElement('div');
                notification.className = 'fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-red-50 text-red-800 px-4 py-2 rounded-lg shadow-lg z-50 border border-red-200';
                notification.innerHTML = `
                  <div class="flex items-center">
                    <i class="fa-solid fa-exclamation-circle mr-2"></i>
                    <span>无法返回上一页，您可能已经在浏览的起始页</span>
                  </div>
                `;
                document.body.appendChild(notification);
                
                // 3秒后移除通知
                setTimeout(() => {
                  notification.style.opacity = '0';
                  setTimeout(() => {
                    document.body.removeChild(notification);
                  }, 500);
                }, 3000);
              };
              
              // 延迟显示通知，确保DOM已经加载
              setTimeout(showErrorNotification, 500);
            } else {
              // 不在管理端时重定向到管理端登录
              setTimeout(() => {
                window.location.replace('/admin/login');
              }, 100);
            }
          } catch (error) {
            console.error('处理Cannot go back错误时发生异常:', error);
            setTimeout(() => {
              window.location.href = '/admin/login';
            }, 100);
          }
        } else if (errorMessage.includes('Failed to fetch dynamically imported module') || 
                 errorMessage.includes('Unexpected token')) {
        // 处理组件加载失败的情况
        console.error('检测到组件加载失败，清理缓存并刷新');
        try {
          localStorage.removeItem('auth');
          localStorage.removeItem('authToken');
          setTimeout(() => {
            window.location.reload();
          }, 100);
        } catch (e) {
          console.error('清理缓存失败:', e);
          window.location.reload();
        }
      }
      // 其他可能导致卡死的错误
      else if (errorMessage.includes('Cannot read properties of null') || 
               errorMessage.includes('Cannot read properties of undefined')) {
        console.error('检测到可能导致界面卡死的错误，建议刷新页面');
      }
    }
  });
  
     // 添加全局未处理的Promise拒绝捕获
  window.addEventListener('unhandledrejection', (event) => {
    console.error('未处理的Promise拒绝:', event.reason);
    // 处理Cannot go back错误
    if (event.reason && event.reason.message && event.reason.message.includes('Cannot go back')) {
      console.error('捕获到Cannot go back错误，防止页面崩溃');
      // 防止页面崩溃的处理方法
      try {
        // 检查历史记录长度，避免继续尝试返回
        if (window.history && window.history.length <= 1) {
          console.log('历史记录为空，防止继续尝试返回上一页');
          
          // 检查当前是否已经在首页，避免循环重定向
          if (window.location.pathname === '/admin' || window.location.pathname === '/admin/login') {
            console.log('当前已在管理端，不进行重定向操作');
            // 显示一个友好的提示，而不是尝试重定向
            const showErrorNotification = () => {
              const notification = document.createElement('div');
              notification.className = 'fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-red-50 text-red-800 px-4 py-2 rounded-lg shadow-lg z-50 border border-red-200';
              notification.innerHTML = `
                <div class="flex items-center">
                  <i class="fa-solid fa-exclamation-circle mr-2"></i>
                  <span>无法返回上一页，您可能已经在浏览的起始页</span>
                </div>
              `;
              document.body.appendChild(notification);
              
              // 3秒后移除通知
              setTimeout(() => {
                notification.style.opacity = '0';
                setTimeout(() => {
                  document.body.removeChild(notification);
                }, 500);
              }, 3000);
            };
            
            // 延迟显示通知，确保DOM已经加载
            setTimeout(showErrorNotification, 500);
          } else {
            setTimeout(() => {
              window.location.replace('/admin/login');
            }, 100);
          }
        }
      } catch (error) {
        console.error('处理Cannot go back错误时发生异常:', error);
        setTimeout(() => {
          window.location.href = '/admin/login';
        }, 100);
      }
    }
  });
 
  root.render(renderApp());
}

// 开发环境 HMR 时清理对 __reactRoot 的引用，下次冷启动可重新 createRoot（可选）
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    try {
      delete (window as any).__reactRoot;
    } catch (_) {}
  });
}